import { Injectable, inject } from '@angular/core';
import { CalendarService } from '../../../../services/calendar.service';
import { ITimeBlockComponentItem } from '../../types/time-block-component-item-types';
import { ParallelTimeBlockWeekViewVariableIntervalCalculatorService } from './parallel-time-block-geometry/parallel-time-block-week-view-variable-interval-calculator-service';
import { ParallelTimeBlockWeekViewFixIntervalCalculatorService } from './parallel-time-block-geometry/parallel-time-block-week-view-fix-interval-calculator.service';
import { TimeBlockCrudService } from '../../crud/time-block-crud.service';
import { CalendarViews } from '../../../../calendar-types';
import { ParallelTimeBlockMonthViewCalculatorService } from './parallel-time-block-geometry/parallel-time-block-month-view-calculator.service';
import { IFixIntervalViewType, TimeBlockType } from '../../types/time-block-types';
import { TimeBlockStructureService } from '../../time-block-structure/time-block-structure.service';
import { TimeBlockItemBuilderService } from '../../generation/time-block-item-builder.service';

@Injectable()
export class ParallelTimeBlockGeometryCalculatorService {
  private readonly calendarService = inject(CalendarService);
  private readonly timeBlockStructureService = inject(TimeBlockStructureService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly timeBlockCrudService = inject(TimeBlockCrudService);
  private readonly parallelTimeBlockWeekViewVariableIntervalCalculatorService = inject(
    ParallelTimeBlockWeekViewVariableIntervalCalculatorService,
  );
  private readonly parallelTimeBlockWeekViewFixIntervalCalculatorService = inject(
    ParallelTimeBlockWeekViewFixIntervalCalculatorService,
  );
  private readonly parallelTimeBlockMonthViewCalculator = inject(
    ParallelTimeBlockMonthViewCalculatorService,
  );

  public updateParallelTimeBlockGeometries(
    isTimeBlockFixInterval?: boolean,
  ): ITimeBlockComponentItem[] {
    const viewMode = this.calendarService.model.calendarViewMode;

    let updatedTimeBlocks: ITimeBlockComponentItem[];
    if (viewMode === CalendarViews.DayGrid || viewMode === CalendarViews.WeekGrid) {
      updatedTimeBlocks = isTimeBlockFixInterval
        ? this.updateWeekViewFixIntervalGeometry()
        : this.updateWeekViewVariableIntervalGeometry();
    } else {
      updatedTimeBlocks = this.updateMonthViewVariableIntervalAndFixIntervalGeometry();
    }

    if (updatedTimeBlocks && updatedTimeBlocks.length > 0) {
      updatedTimeBlocks.forEach((updatedTimeBlock) => {
        // If the time block is a full day time block, set the new parallel index also for the corresponding time block parts.
        if (
          viewMode === CalendarViews.MonthGrid &&
          updatedTimeBlock.timeBlockModel.fixInterval !== null
        ) {
          const timeBlockParts = this.timeBlockStructureService.retrieveTimeBlockPart(
            updatedTimeBlock.id,
            TimeBlockType.ExistingBlock,
            -1,
          ) as ITimeBlockComponentItem[];
          timeBlockParts.forEach((timeBlockPart) => {
            const clone = timeBlockPart.clone(this.timeBlockItemBuilderService);
            (clone.timeBlockModel.timeBlockViewType as IFixIntervalViewType).parallelIndex = (
              updatedTimeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType
            ).parallelIndex;
            this.timeBlockCrudService.replaceTimeBlock(clone);
          });
        } else {
          // Don't set the parallel index for inner day time blocks
          this.timeBlockCrudService.replaceTimeBlock(updatedTimeBlock);
        }
      });
    }

    return updatedTimeBlocks;
  }

  private updateWeekViewFixIntervalGeometry(): ITimeBlockComponentItem[] {
    return this.parallelTimeBlockWeekViewFixIntervalCalculatorService.calculate();
  }

  private updateWeekViewVariableIntervalGeometry(): ITimeBlockComponentItem[] {
    return this.parallelTimeBlockWeekViewVariableIntervalCalculatorService.calculate();
  }

  private updateMonthViewVariableIntervalAndFixIntervalGeometry(): ITimeBlockComponentItem[] {
    return this.parallelTimeBlockMonthViewCalculator.calculate();
  }
}
